/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import React from 'react';

import classnames from 'classnames';

import { Button } from '@msdyn365-commerce-modules/utilities';

/**
 * Class names used in component.
 */
const componentClassName = 'msc-action-menu';
const toggleMenuButtonClassName = `${componentClassName}__action-toggle`;
const actionsContainerClassName = `${componentClassName}__actions-container`;

/**
 * IActionMenuComponent interface.
 */
interface IActionMenuComponent extends IComponent<IActionMenuProps> {}

/**
 * IActionMenuProps interface.
 */
export interface IActionMenuProps extends IComponentProps<{}> {
    className?: string;
    isVisible?: boolean;
    isDisabled?: boolean;
    label?: string;
    ariaLabel?: string;
    title?: string;
    children?: React.ReactNode | React.ReactNode[];
    telemetryAttributes?: { [key: string]: string };
    onClick?: (showActions: boolean) => void;
}

let setShowPreviousActions: React.Dispatch<React.SetStateAction<boolean>> | undefined;

/**
 * Menu function.
 * @param isShowActions - Whether actions should be visible or not.
 * @param setShowActions - Function to change the state.
 */
const toggle = (isShowActions: boolean, setShowActions: React.Dispatch<React.SetStateAction<boolean>>) => {
    const shouldShowActions = !isShowActions;
    if (shouldShowActions) {
        if (setShowPreviousActions) {
            setShowPreviousActions(false);
        }
        setShowActions(shouldShowActions);
        setShowPreviousActions = setShowActions;
    } else {
        setShowPreviousActions = undefined;
        setShowActions(shouldShowActions);
    }
};

/**
 * ActionMenu component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const ActionMenuFunctionalComponent: React.FC<IActionMenuProps> = (props: IActionMenuProps) => {
    const [showActions, setShowActions] = React.useState(false);

    let children = props.children ?? [];
    children = Array.isArray(children) ? children : [children];

    const onClickHandler = (): void => {
        toggle(showActions, setShowActions);
        props.onClick?.(showActions);
    };

    const isVisible = props.isVisible ?? true;
    if (!isVisible) {
        return <></>;
    }

    return (
        <div className={classnames(props.className, componentClassName)}>
            <Button
                className={classnames(
                    toggleMenuButtonClassName,
                    showActions ? `${toggleMenuButtonClassName}-active` : '',
                    props.isDisabled ? `${toggleMenuButtonClassName}-disabled` : ''
                )}
                title={props.title ?? ''}
                disabled={!!props.isDisabled}
                aria-label={props.ariaLabel ?? props.label ?? ''}
                aria-disabled={!!props.isDisabled}
                onClick={onClickHandler}
                {...props.telemetryAttributes}
            >
                {props.label}
            </Button>
            {showActions && <div className={classnames(actionsContainerClassName)}>{children}</div>}
        </div>
    );
};

/**
 * ActionMenu component.
 */
export const ActionMenuComponent: React.FunctionComponent<IActionMenuProps> = msdyn365Commerce.createComponent<IActionMenuComponent>(
    'ActionMenuComponent',
    { component: ActionMenuFunctionalComponent }
);
